// src/Pages/Tietomeista/TietoaMeista.jsx
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "@dr.pogodin/react-helmet";
import { useTheme } from "../../context/ThemeContext";
import AOS from "aos";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
// POISTETTU: import HeroKenBurns from "../../components/HeroKenBurns/HeroKenBurns.jsx";
import WebDesignBanner from "../../components/HeaderWithAd/HeaderWithAd";
import usePublicStats from "../../hooks/usePublicStats";

import rot1 from "../../assets/tietomeista_arttu.webp";
// import rot2 from "../../assets/tietomeista_arttu";
// import rot3 from "../../assets/tietomeista_arttu";

// HERO-kuvat (dark + light)
import aboutStageDark from "../../assets/tietoameistaheader.png";
import aboutStageLight from "../../assets/tietoameistaheaderlight.webp";

// Tekijä-kuva
import tekija2 from "../../assets/arttu.png";

/* =========================================================
   Utilit
   ========================================================= */
// Käyttäjän esteettömyysasetus: vähennetty liike
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(!!mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

// Triggaa true kun elementti näkyy viewportsissa (kerran)
function useInViewOnce(options = { threshold: 0.3, rootMargin: "0px" }) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current || seen) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setSeen(true);
      });
    }, options);
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [seen, options]);
  return { ref, seen };
}

// Kevyt numeron animointi
function useCountUp({ to = 0, duration = 1500, start = false }) {
  const [val, setVal] = useState(0);
  const startRef = useRef(0);
  const fromRef = useRef(0);
  useEffect(() => {
    if (!start) return;
    let raf = 0;
    const startTime = performance.now();
    startRef.current = startTime;
    const from = fromRef.current;
    const delta = Math.max(0, to - from);
    const loop = (t) => {
      const p = Math.min(1, (t - startTime) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setVal(Math.round(from + delta * eased));
      if (p < 1) raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [to, duration, start]);
  useEffect(() => {
    fromRef.current = 0;
  }, [to]);
  return val;
}

// Koko sivun tulofade
function useMountFade(disabled = false, delayMs = 80) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (disabled) {
      setReady(true);
      return;
    }
    const start = () => setReady(true);
    const id = delayMs ? setTimeout(start, delayMs) : requestAnimationFrame(start);
    return () => (delayMs ? clearTimeout(id) : cancelAnimationFrame(id));
  }, [disabled, delayMs]);
  return ready;
}

/**
 * RotatingBanner: kuvasarja crossfade-efektillä.
 * - Kunnioittaa prefers-reduced-motion.
 * - Skaalautuu tasaisesti: fixed height + per-kuva fokuspiste.
 */
function RotatingBanner({
  images = [],
  focals = [],
  isDark = false,
  interval = 5000,
  className = "",
  fallbackFocal = [50, 45],
}) {
  const [idx, setIdx] = React.useState(0);

  React.useEffect(() => {
    if (!images || images.length <= 1) return;
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (mq && mq.matches) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % images.length), Math.max(2500, interval));
    return () => clearInterval(id);
  }, [images, interval]);

  return (
    <div
      className={`relative w-full rounded-2xl overflow-hidden ring-1 ${
        isDark ? "ring-white/10" : "ring-black/10"
      } ${className}`}
      style={{ height: "clamp(240px, 28vw, 420px)" }}
      aria-hidden="true"
    >
      {images.map((src, i) => {
        const f = Array.isArray(focals[i]) ? focals[i] : fallbackFocal; // [x,y]
        const objectPosition = `${f[0]}% ${f[1]}%`;
        return (
          <img
            key={i}
            src={src}
            alt=""
            loading={i === 0 ? "eager" : "lazy"}
            style={{ objectPosition }}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
              i === idx ? "opacity-100" : "opacity-0"
            }`}
          />
        );
      })}
      <div
        className={`absolute inset-0 bg-gradient-to-b ${
          isDark ? "from-black/40 via-black/25 to-transparent" : "from-white/35 via-white/15 to-transparent"
        }`}
      />
    </div>
  );
}

/* =========================================================
   Sivu
   ========================================================= */
export default function TietoaMeista() {
  const { theme } = useTheme();
  const prefersReduced = usePrefersReducedMotion();
  const pageReady = useMountFade(prefersReduced, 80);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  const isDark = theme === "dark";

  // Valitse hero-kuva teeman mukaan (käytetään OG/Helmetissa)
  const heroImage = isDark ? aboutStageDark : aboutStageLight;

  // Preload (pienentää välähdyksiä teeman vaihdossa)
  React.useEffect(() => {
    const img = new Image();
    img.src = heroImage;
  }, [heroImage]);

  const cardBg = isDark ? "bg-[#0b0b0f]/60 ring-white/10" : "bg-lemon/60 ring-white/40";
  const bodyText = isDark ? "text-white/85" : "text-black/80";

  // Mittarit backista
  const { data: stats, loading: statsLoading } = usePublicStats(); // {bookings_total, quotes_total, artists_total}
  // Starttaa hieman ennen kuin kortti on täysin näkyvissä
  const { ref: heroRef, seen } = useInViewOnce({ threshold: 0.2, rootMargin: "0px 0px -15% 0px" });
  const animate = seen && !prefersReduced && !statsLoading;

  const bookingsAnim = useCountUp({ to: stats.bookings_total ?? 0, duration: 1200, start: animate });
  const quotesAnim   = useCountUp({ to: stats.quotes_total   ?? 0, duration: 1400, start: animate });
  const artistsAnim  = useCountUp({ to: stats.artists_total  ?? 0, duration: 1600, start: animate });

  const bookings = prefersReduced || statsLoading ? (stats.bookings_total ?? 0) : bookingsAnim;
  const quotes   = prefersReduced || statsLoading ? (stats.quotes_total   ?? 0) : quotesAnim;
  const artists  = prefersReduced || statsLoading ? (stats.artists_total  ?? 0) : artistsAnim;

  return (
    <>
      <Helmet>
          {/* Title voidaan pitää clientissä. Kaikki muut metas (description/OG/Twitter)
            tulevat palvelimelta (BBX SEO CORE) → vältetään duplikaatio. */}
        <title>Tietoa meistä – Buukkeri</title>
      </Helmet>

      {/* Wrap: koko sivun tulofade */}
      <div
        className={`will-change-opacity will-change-transform
          transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]
          ${pageReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
      >
        {/* HERO — sama kuori kuin etusivulla */}
        <WebDesignBanner>

          <div className="text-center">
            <h1
              className="mb-2 font-customFont2 dark:text-white/90 text-white/90
                         text-4xl sm:text-5xl lg:text-6xl leading-tight
                         dark:text-shadow-lg"
              data-aos="fade-up"
            >
              Tietoa meistä
            </h1>

            <p
              className="mt-2 dark:text-white/80 text-white/80 font-customFont1 text-lg sm:text-xl text-shadow-lg font-semibold"
              data-aos="fade-up"
              data-aos-delay="120"
            >
              Luotettava kumppani esiintyjän buukkaamiseen.
            </p>
          </div>

          {/* Mittarit kortissa kuten etusivulla */}
          {/* Sentineli, joka on aina läsnä (ei display:none) → käynnistää animoinnin myös mobiilissa */}
          <div
            ref={heroRef}
            className="h-1 w-full opacity-0 pointer-events-none"
            aria-hidden="true"
          />

          {/* Mittarit kortissa kuten etusivulla – piiloon mobiilissa */}
          <div
               className="relative z-30 overflow-visible mx-auto w-full max-w-3xl
                       rounded-2xl p-4 sm:p-6 bg-lemon/60 dark:bg-black/40
                       backdrop-blur-md shadow-lg ring-1 ring-white/40 mt-5"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" aria-label="Tunnusluvut">
              <StatCard label="Keikkoja buukattu" value={bookings} isDark={isDark} />
              <StatCard label="Tarjouksia kyselty" value={quotes}   isDark={isDark} />
              <StatCard label="Esiintyjiä"         value={artists}  isDark={isDark} />
            </div>
          </div>
        </WebDesignBanner>

        {/* TARINA + TEKIJÄ */}
        <main className="relative w-full pb-16">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <section className={`mt-8 rounded-2xl ${cardBg} backdrop-blur-md shadow-lg ring-1 p-0 overflow-hidden`} data-aos="fade-up">
              {/* Vaihtuva banneri */}
              <div className="p-4 pb-0">
                <RotatingBanner
                  images={[rot1]}
                  focals={[
                    [50, 25],
                    [55, 8],
                    [48, 12],
                  ]}
                  isDark={isDark}
                  interval={6000}
                  className=""
                />
              </div>

              {/* Header-chippi kuvan reunalle */}
              <div className="relative -mt-12 sm:-mt-14 px-6 flex items-center gap-4">
                <img
                  src={tekija2}
                  alt="Arttu – kehittäjä & rumpali"
                  width={144}
                  height={144}
                  className="w-32 h-32 rounded-full object-cover object-[50%_2%] ring-4 ring-primary shadow-[0_0_30px_rgba(49,255,217,0.35)] bg-black/20"
                  loading="eager"
                  decoding="async"
                />
                <div className="flex-1 mt-12">
                  <h2 className="font-customFont2 dark:text-white/90 text-black/90 text-lg font-semibold">Palvelun kehittäjä</h2>
                  <p className={`${bodyText} mt-1 text-sm sm:text-base opacity-90 font-semibold`}>Arttu Stenius</p>
                </div>
              </div>

              {/* Sisältö: kaksi palstaa isolla näytöllä */}
              <div className="px-6 pb-6 pt-3">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                  {/* Tarina */}
                  <div className="lg:col-span-7">
                   <p className={`mt-1 ${bodyText} font-customFont1 text-sm sm:text-base`}>
                    Buukkeri syntyi yhdestä bändistä ja yhdestä buukkauslomakkeesta.{" "}
                    Vähemmän arvailua, enemmän keikkoja. <br />
                    <br />
                    Palvelun kehittäjä <strong>Arttu Stenius</strong> on bilebändin rumpali, joka
                    kyllästyi ikuisuuksiin venyviin sähköpostiketjuihin tyyliin:
                    ”mitä maksaa / missä / milloin / entä jos?”. Ratkaisuksi hän rakensi ensin
                    <strong> oman bändinsä Joyriden sivuille automaattisen tarjouslomakkeen</strong>,
                    joka laski hinnan ja varmisti, onko keikkapäivä vapaa. <br />
                    <br />
                    Kun lomake toimi, se siirrettiin laajempaan käyttöön
                    <strong> Spiderlegs Productions</strong> -sivulle ja sisäiseen kehitysprojektiin,
                    joka kulki työnimellä <strong>Bileboxi</strong>. Siinä testattiin käytännössä
                    eri keikkapituuksia, hinnoittelua ja alueita – alusta asti muusikon arki
                    mielessä. <br />
                    <br />
                    Lopulta oli selvää, että järjestelmä ei ollut enää vain yhden bändin
                    apuväline, vaan <strong>tarvittiin oikea buukkausalusta esiintyjille</strong>.{" "}
                    Näin syntyi <strong>Buukkeri</strong>: palvelu, joka ymmärtää sekä bändin
                    liiderin todellisuuden että keikan tilaajan tarpeet. Tavoite on
                    yksinkertainen: tee varauksesta helppo ja nopea. Mitä sitä turhaan jauhamaan –
                    mitä maksaa keikka? Tuossa hinta. Nähdään illalla lavalla!
                  </p>

                  </div>

                  {/* YHTEYSTIEDOT – responsiivinen paneeli */}
                  <div className="lg:col-span-5">
                    <div
                      className={`rounded-xl ${isDark ? "bg-black/35 ring-white/10" : "bg-white/60 ring-black/10"} backdrop-blur-md ring-1 p-4`}
                      data-aos="fade-up"
                    >
                      <h3 className={`font-customFont2 ${isDark ? "text-white/90" : "text-black/90"} text-lg font-semibold`}>Yhteystiedot</h3>

                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {/* Puhelin */}
                        <a
                          href="tel:+358443519265"
                          className={`flex items-start gap-3 rounded-lg p-3 ring-1 transition ${
                            isDark ? "ring-white/10 bg-white/5 hover:bg-white/10 text-white/85" : "ring-black/10 bg-white/90 hover:bg-white text-black/80"
                          }`}
                          aria-label="Soita"
                        >
                          <span className={`mt-0.5 inline-flex rounded-md p-2 ${isDark ? "bg-white/10" : "bg-black/5"}`}>
                            <Phone size={18} />
                          </span>
                          <span className="text-sm leading-5">
                            <span className="block font-semibold">Puhelin</span>
                            <span className="opacity-80">+358 44 351 92 65</span>
                          </span>
                        </a>

                        {/* Sähköposti */}
                        <a
                          href="mailto:info@buukkeri.fi"
                          className={`flex items-start gap-3 rounded-lg p-3 ring-1 transition ${
                            isDark ? "ring-white/10 bg-white/5 hover:bg-white/10 text-white/85" : "ring-black/10 bg-white/90 hover:bg-white text-black/80"
                          }`}
                          aria-label="Lähetä sähköpostia"
                        >
                          <span className={`mt-0.5 inline-flex rounded-md p-2 ${isDark ? "bg-white/10" : "bg-black/5"}`}>
                            <Mail size={18} />
                          </span>
                          <span className="text-sm leading-5">
                            <span className="block font-semibold">Sähköposti</span>
                            <span className="opacity-80 break-all">info@buukkeri.fi</span>
                          </span>
                        </a>

                        {/* Sijainti */}
                        <a
                          href="https://maps.google.com/?q=Kerava,+Suomi"
                          target="_blank"
                          rel="noreferrer"
                          className={`flex items-start gap-3 rounded-lg p-3 ring-1 transition ${
                            isDark ? "ring-white/10 bg-white/5 hover:bg-white/10 text-white/85" : "ring-black/10 bg-white/90 hover:bg-white text-black/80"
                          }`}
                          aria-label="Katso kartalla"
                        >
                          <span className={`mt-0.5 inline-flex rounded-md p-2 ${isDark ? "bg-white/10" : "bg-black/5"}`}>
                            <MapPin size={18} />
                          </span>
                          <span className="text-sm leading-5">
                            <span className="block font-semibold">Sijainti</span>
                            <span className="opacity-80">Kerava, Suomi</span>
                          </span>
                        </a>

                        {/* Aukiolo / tavoitettavuus */}
                        <div
                          className={`flex items-start gap-3 rounded-lg p-3 ring-1 ${
                            isDark ? "ring-white/10 bg-white/5 text-white/85" : "ring-black/10 bg-white/90 text-black/80"
                          }`}
                          role="group"
                          aria-label="Tavoitettavuus"
                        >
                          <span className={`mt-0.5 inline-flex rounded-md p-2 ${isDark ? "bg-white/10" : "bg-black/5"}`}>
                            <Clock size={18} />
                          </span>
                          <span className="text-sm leading-5">
                            <span className="block font-semibold">Tavoitettavuus</span>
                            <span className="opacity-80">arkisin 9–17</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>
    </>
  );
}

/* =========================================================
   Pieni presentaatio-komponentti yhdelle mittarille
   ========================================================= */
function StatCard({ label, value, suffix = "", isDark }) {
  return (
    <div
      className={`rounded-xl ${isDark ? "bg-black/35 ring-white/10" : "bg-white/55 ring-black/10"}
                  backdrop-blur-md ring-1 shadow-sm px-4 py-3 text-center font-semibold`}
      role="group"
      aria-label={label}
    >
      <div className={`font-customFont2 ${isDark ? "text-white" : "text-black/80"} text-2xl leading-none`}>
       <span aria-live="polite" aria-atomic="true">{value}</span>
        {suffix && <span className="opacity-80 ml-0.5">{suffix}</span>}
      </div>
      <div className={`mt-1 ${isDark ? "text-white/70" : "text-black/70"} text-xs tracking-wide`}>{label}</div>
    </div>
  );
}
